import { CheckCircle2, Clock3, MessageCircle, Save, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { ChoiceCard } from '../components/ChoiceCard';
import { SectionHeading } from '../components/SectionHeading';
import { birthdayConfig, type Hotel } from '../config';
import { useLocalStorage } from '../hooks/useLocalStorage';

export type DinnerPlan = {
  atmosphere: string;
  cuisine: string;
  decoration: string[];
  time: string;
  customTime: string;
  extras: string[];
};

const emptyPlan: DinnerPlan = {
  atmosphere: '',
  cuisine: '',
  decoration: [],
  time: '',
  customTime: '',
  extras: [],
};

function toggleItem(items: string[], value: string) {
  return items.includes(value) ? items.filter((item) => item !== value) : [...items, value];
}

export function DinnerPlannerSection({ selectedHotel }: { selectedHotel: Hotel | null }) {
  const [plan, setPlan] = useLocalStorage<DinnerPlan>('birthday-dinner-plan', emptyPlan);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!saved) return;
    const timer = window.setTimeout(() => setSaved(false), 2500);
    return () => window.clearTimeout(timer);
  }, [saved]);

  const resolvedTime = plan.time === 'Custom time' ? plan.customTime || 'Custom time not entered' : plan.time;
  const whatsappText = `Hi! This is my selected birthday dinner plan:\n\nHotel/Venue: ${selectedHotel?.name || 'Not selected yet'}\nAtmosphere: ${plan.atmosphere || 'Not selected'}\nCuisine: ${plan.cuisine || 'Not selected'}\nDecoration: ${plan.decoration.join(', ') || 'Not selected'}\nTime: ${resolvedTime || 'Not selected'}\nExtras: ${plan.extras.join(', ') || 'None'}\n\nI choose this plan 💖`;
  const cleanNumber = birthdayConfig.sender.whatsappNumber.replace(/\D/g, '');
  const whatsappUrl = cleanNumber && !cleanNumber.includes('X')
    ? `https://wa.me/${cleanNumber}?text=${encodeURIComponent(whatsappText)}`
    : `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;

  return (
    <section id="dinner" className="section-shell dinner-section">
      <div className="page-container">
        <SectionHeading
          eyebrow={birthdayConfig.copy.dinner.eyebrow}
          title={birthdayConfig.copy.dinner.title}
          description={birthdayConfig.copy.dinner.description}
        />

        <div className="planner-layout">
          <div className="planner-options">
            <OptionGroup title="Dinner atmosphere" items={birthdayConfig.dinner.atmospheres} selected={[plan.atmosphere]} onSelect={(value) => setPlan({ ...plan, atmosphere: value })} />
            <OptionGroup title="Cuisine" items={birthdayConfig.dinner.cuisines} selected={[plan.cuisine]} onSelect={(value) => setPlan({ ...plan, cuisine: value })} />
            <OptionGroup title="Table decoration" items={birthdayConfig.dinner.decorations} selected={plan.decoration} multiple onSelect={(value) => setPlan({ ...plan, decoration: toggleItem(plan.decoration, value) })} />
            <OptionGroup title="Preferred time" items={birthdayConfig.dinner.times} selected={[plan.time]} onSelect={(value) => setPlan({ ...plan, time: value })} />
            {plan.time === 'Custom time' && (
              <label className="custom-time-field">
                <Clock3 size={18} />
                <span>Enter your preferred time</span>
                <input type="time" value={plan.customTime} onChange={(event) => setPlan({ ...plan, customTime: event.target.value })} />
              </label>
            )}
            <OptionGroup title="Optional additions" items={birthdayConfig.dinner.extras} selected={plan.extras} multiple onSelect={(value) => setPlan({ ...plan, extras: toggleItem(plan.extras, value) })} />
          </div>

          <motion.aside id="dinner-summary" className="plan-summary glass" initial={{ opacity: 0, x: 28 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="summary-icon"><Sparkles /></div>
            <span className="eyebrow">{birthdayConfig.copy.dinner.summaryEyebrow}</span>
            <h3>{birthdayConfig.copy.dinner.summaryTitle}</h3>
            <SummaryRow label="Hotel / Venue" value={selectedHotel?.name || 'Choose from the hotel section'} />
            <SummaryRow label="Atmosphere" value={plan.atmosphere} />
            <SummaryRow label="Cuisine" value={plan.cuisine} />
            <SummaryRow label="Decoration" value={plan.decoration.join(', ')} />
            <SummaryRow label="Time" value={resolvedTime} />
            <SummaryRow label="Extras" value={plan.extras.join(', ')} />
            <div className="summary-actions">
              <button className="secondary-button" onClick={() => setSaved(true)}><Save size={18} /> {birthdayConfig.copy.dinner.saveButton}</button>
              <a className="primary-button" href={whatsappUrl} target="_blank" rel="noreferrer"><MessageCircle size={18} /> {birthdayConfig.copy.dinner.whatsappButton}</a>
            </div>
            {saved && <div className="saved-note" role="status"><CheckCircle2 size={17} /> Your choices are saved on this device.</div>}
            <small>{birthdayConfig.copy.dinner.disclaimer}</small>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}

function OptionGroup({ title, items, selected, multiple = false, onSelect }: { title: string; items: string[]; selected: string[]; multiple?: boolean; onSelect: (value: string) => void }) {
  return (
    <fieldset className="option-group">
      <legend>{title}</legend>
      <div className="choice-grid">
        {items.map((item) => <ChoiceCard key={item} label={item} selected={selected.includes(item)} onClick={() => onSelect(item)} multiple={multiple} />)}
      </div>
    </fieldset>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return <div className="summary-row"><span>{label}</span><strong>{value || 'Not selected'}</strong></div>;
}
